import React, { useState, useRef, useEffect, useContext } from "react";
import styles from "./ActionReplays.module.scss";
import TinySlider from "tiny-slider-react";
import SliderControls from "./SliderButton";
import { isMobile } from "react-device-detect";
import ActionReplayPlaceholder from "../components/ActionReplayPlaceholder";
import { AzureMP } from "react-azure-mp";
import { Context } from "../Store";
import ReactPlayer from "react-player/lazy";
import { eventNames } from "../utils/eventData";
import { capitalize, trackEvent } from "../utils/helpers";
import ShareArrow from "./ShareArrow";
import CopiedContext from "../CopiedContext";
import IsCopiedMessage from "./IsCopiedMessage";

const gameByEventType = {
  50: "Amplifier",
  51: "Amplifier",
  100: "Short Circuit",
  155: "Territory",
  200: "Eclipse",
  201: "Eclipse",
  202: "Eclipse",
  400: "Studio 21",
  450: "Studio 21",
};

const getVideoGame = (video) => gameByEventType[video.type] ?? video.game;

// Inner slider component to enable clean remounting when filter changes
const ActionReplaySlider = ({ videos, players, handleHover, openLightbox, state }) => {
  const sliderRef = useRef();
  const sliderId = useRef(`slider-${Math.random().toString(36).substr(2, 9)}`).current;
  const hasTrackedScrollRef = useRef(false);
  const [isFirst, setIsFirst] = useState(true);
  const [isLast, setIsLast] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [itemsCount, setItemsCount] = useState(1);

  useEffect(() => {
    const slider = sliderRef.current?.slider;
    if (!slider) return;

    const isLooping = videos.length >= 3;

    const updateIndex = (info) => {
      if (isLooping) {
        setIsFirst(false);
        setIsLast(false);
      } else {
        setIsFirst(info.index === 0);
        setIsLast(info.index + info.items >= info.slideCount);
      }
      setCurrentIndex((info.displayIndex ?? info.index + 1) - 1);
      setItemsCount(info.items);
    };

    updateIndex(slider.getInfo());
    slider.events.on("indexChanged", updateIndex);

    // Handle clicks on cloned slides in loop mode — clones aren't in React's fiber tree
    const container = slider.getInfo().container;
    const handleCloneClick = (e) => {
      if (!e.target.closest(".tns-slide-cloned")) return;
      const target = e.target.closest("[data-replay-index]");
      if (!target) return;
      openLightbox(parseInt(target.getAttribute("data-replay-index"), 10));
    };
    container.addEventListener("click", handleCloneClick);

    return () => {
      slider.events.off("indexChanged", updateIndex);
      container.removeEventListener("click", handleCloneClick);
    };
  }, [openLightbox, videos.length]);

  const activeIndex = currentIndex + Math.floor(itemsCount / 2);

  const settings = {
    lazyload: true,
    touch: true,
    nav: false,
    controls: false,
    mouseDrag: true,
    loop: videos.length >= 3,
    items: 1,
    edgePadding: isMobile && videos.length > 1 ? 30 : 0,
    gutter: 16,
    responsive: {
      640: {
        items: 1,
        edgePadding: videos.length >= 3 ? 80 : 0,
        gutter: 16,
      },
      1024: {
        items: videos.length >= 3 ? 1 : Math.min(videos.length, 2),
        edgePadding: videos.length >= 3 ? 280 : 0,
        gutter: 20,
      },
      1440: {
        items: videos.length >= 3 ? 3 : Math.min(videos.length, 2),
        edgePadding: videos.length >= 3 ? 60 : 0,
        gutter: 20,
      },
    },
  };

  return (
    <div className={styles.sliderWrapper}>
      <div
        id={sliderId}
        className={styles.sliderContainer}
        style={
          isMobile && videos.length === 1
            ? { padding: "0 30px" }
            : !isMobile && videos.length < 3
              ? { maxWidth: videos.length === 1 ? "711px" : "1200px", margin: "0 auto" }
              : undefined
        }
      >
        {videos.length >= 3 && <SliderControls slider={sliderRef} isFirst={isFirst} isLast={isLast} compact />}

        <TinySlider
          settings={settings}
          ref={sliderRef}
          onIndexChanged={() => {
            if (!hasTrackedScrollRef.current) {
              hasTrackedScrollRef.current = true;
              trackEvent("Gallery Scroll", "action_replays_scroll");
            }
          }}
        >
          {videos.map((video, index) => (
            <div key={index} data-index={index} style={{ position: "relative" }} className={styles.actionReplayItem}>
              <div
                data-replay-index={index}
                onClick={() => openLightbox(index)}
                onMouseEnter={!isMobile && index === activeIndex ? (e) => handleHover(index, e) : undefined}
                onMouseLeave={!isMobile ? (e) => handleHover(index, e) : undefined}
                style={{ cursor: "pointer" }}
              >
                {!video.thumbnail_url && <ActionReplayPlaceholder video={{ video }} players={{ players }} />}
                {video.thumbnail_url && (
                  <div
                    className={`${styles.thumbnailWrapper}${isMobile && index === activeIndex ? ` ${styles.thumbnailActive}` : ""}`}
                  >
                    <img src={video.thumbnail_url} alt="" />
                  </div>
                )}
              </div>

              <div
                className={`${styles.slideExtras}${videos.length >= 3 && index !== activeIndex ? ` ${styles.slideExtrasHidden}` : ""}`}
              >
                <ShareArrow
                  title={`${capitalize(players.find((p) => p.id === Number(video.players))?.name)}: ${eventNames[video.type]}`}
                  url={video.video_url}
                  text={`Check out this ${eventNames[video.type]} Action Replay from our Electric Shuffle visit.`}
                  downloadUrl={video.video_url}
                  isVideo={true}
                  customBottom="0px"
                  sectionName="Action Replay"
                  mediaShare={{
                    mediaType: "VIDEO",
                    mediaURI: video.asset_uri,
                    videoURL: video.video_url,
                    sectionName: "Action Replay",
                    thumbnail: video.thumbnail_url,
                    videoType: video.type,
                  }}
                  cityurl={state.cityurl}
                  venue={state.venue}
                  azureurl={state.azureurl}
                  videourl={state.videourl}
                  guid={state.guid}
                  country={state.country}
                />
                <div className={styles.actionReplayText}>
                  <span className={styles.actionReplayTitle}>
                    {capitalize(players.find((p) => p.id === Number(video.players))?.name)}: {eventNames[video.type]}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </TinySlider>
      </div>
      {(videos.length >= 3 || (videos.length === 2 && isMobile)) && (
        <div className={styles.counterRow}>
          <button
            className={styles.counterArrow}
            onClick={() => sliderRef.current?.slider?.goTo("prev")}
            aria-label="Previous"
          >
            <img src="./img/slide-arrow-prev.svg" alt="" />
          </button>
          <div className={styles.slideCounter}>
            <span>{currentIndex + 1}</span>/{videos.length}
          </div>
          <button
            className={styles.counterArrow}
            onClick={() => sliderRef.current?.slider?.goTo("next")}
            aria-label="Next"
          >
            <img src="./img/slide-arrow-next.svg" alt="" />
          </button>
        </div>
      )}
    </div>
  );
};

const ActionReplays = (props) => {
  const state = useContext(Context);
  const [showCopied, setShowCopied] = useState(false);
  const [selectedPlayerId, setSelectedPlayerId] = useState("all");
  const [selectedGameType, setSelectedGameType] = useState("all");

  const copiedContextValue = { showCopied, setShowCopied };

  const players = Array.isArray(state.players) ? state.players : [];
  const videos = Array.isArray(state.actionReplays) ? state.actionReplays : [];

  const playersWithReplays = players
    .filter((player) => videos.some((video) => Number(video.players) === Number(player.id)))
    .sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }));

  const gameTypesWithReplays = [...new Set(videos.map(getVideoGame).filter(Boolean))];

  const filteredVideos = videos.filter((video) => {
    if (selectedPlayerId !== "all") return Number(video.players) === Number(selectedPlayerId);
    if (selectedGameType !== "all") return getVideoGame(video) === selectedGameType;
    return true;
  });

  const actionReplaysVideos = filteredVideos.map((video) => {
    if (video.video_url.includes(".mp4")) {
      return (
        <div className={styles.actionReplayItem}>
          <ReactPlayer
            className="react-player"
            url={video.video_url}
            muted
            controls
            playing
            width="100%"
            height="100%"
          />
        </div>
      );
    }
    return (
      <div className={styles.actionReplayItem}>
        <AzureMP
          options={{ controls: true, autoplay: true, muted: true }}
          src={[{ src: video.video_url, type: "application/vnd.ms-sstr+xml" }]}
        />
      </div>
    );
  });

  const actionReplaysMedia = filteredVideos.map((video) => ({
    mediaType: "VIDEO",
    mediaURI: video.asset_uri,
    mediaProvider: video.video_provider,
    videoURL: video.video_url,
    sectionName: "Action Replay",
    thumbnail: video.thumbnail_url,
    videoType: video.type,
    eventName: eventNames[video.type],
  }));

  const handleHover = (index, e) => {
    props.handleHover(
      {
        section: "ActionReplay",
        index,
        ...actionReplaysMedia[index],
        target: e.currentTarget,
        timeStamp: e.timeStamp,
        groupLength: actionReplaysMedia.length,
        sources: actionReplaysVideos,
        mediashare: actionReplaysMedia,
      },
      e.type,
    );
  };

  const openLightbox = (index) => {
    props.openLightbox({
      section: "ActionReplay",
      index,
      sources: actionReplaysVideos,
      mediashare: actionReplaysMedia,
    });
  };

  return (
    <CopiedContext.Provider value={copiedContextValue}>
      <div className={`section ${styles.actionReplaysSection}`}>
        <h2>Epic slides only</h2>
        <h3>who nailed it</h3>

        {(playersWithReplays.length > 1 || gameTypesWithReplays.length > 1) && (
          <div className={styles.filterRow}>
            {playersWithReplays.length > 1 && (
              <select
                id="playerFilter"
                className={styles.filterSelect}
                value={selectedPlayerId}
                onChange={(e) => {
                  setSelectedPlayerId(e.target.value);
                  setSelectedGameType("all");
                  trackEvent("Action Replays", "action_replay_filter");
                }}
              >
                <option value="all">All Players</option>
                {playersWithReplays.map((player) => (
                  <option key={player.id} value={player.id}>
                    {capitalize(player.name)}
                  </option>
                ))}
              </select>
            )}

            {gameTypesWithReplays.length > 1 && (
              <select
                id="gameFilter"
                className={styles.filterSelect}
                value={selectedGameType}
                onChange={(e) => {
                  setSelectedGameType(e.target.value);
                  setSelectedPlayerId("all");
                  trackEvent("Action Replays", "action_replay_filter", "Game filter");
                }}
              >
                <option value="all">All Games</option>
                {gameTypesWithReplays.map((game) => (
                  <option key={game} value={game}>
                    {game}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}

        {filteredVideos.length > 0 && (
          <ActionReplaySlider
            key={`${selectedPlayerId}-${selectedGameType}`}
            videos={filteredVideos}
            players={players}
            handleHover={handleHover}
            openLightbox={openLightbox}
            state={state}
          />
        )}
        <IsCopiedMessage />
      </div>
    </CopiedContext.Provider>
  );
};

export default ActionReplays;
